/**
 * Spec 0044 — per-product internal API key management (saas-auth SASATH-0052).
 *
 * SUPER_ADMIN-only section mounted on the product detail screen (the caller in
 * ProductDetail gates rendering on `operator?.isSuperAdmin`; this component
 * additionally degrades to a contained "SUPER_ADMIN required" state if the API
 * returns 403). Self-fetches the key list on mount with isolated
 * loading/empty/error states — it is NOT folded into the shared
 * productDetailLoader, so an ordinary SAAS_ADMIN viewer's product page is never
 * broken by a 403 here.
 *
 * A product may have multiple ACTIVE keys at once (zero-downtime rotation). The
 * issue flow shows the freshly minted raw key EXACTLY ONCE via SecretOnceDialog
 * (copy-to-clipboard + "you won't see this again" warning); on dismiss the raw
 * key is dropped from component state. The raw key is held only in transient
 * state for that single display lifecycle — never persisted, never logged,
 * never written to localStorage/sessionStorage, never sent to telemetry. Revoke
 * ("turn off") is idempotent: a revoked row staying REVOKED is treated as
 * success, not an error.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import { useEffect, useState } from 'react';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type { InternalKeyMetadata, IssueInternalKeyResponse } from '@/api/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorBanner } from '@/components/ErrorBanner';
import { SecretOnceDialog } from '@/components/SecretOnceDialog';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';

function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403;
}

export function ProductInternalKeys({ productId }: { productId: number }) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [rows, setRows] = useState<InternalKeyMetadata[] | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<InternalKeyMetadata | null>(null);
  const [busy, setBusy] = useState(false);
  // The one-time raw key — transient display state only. Never persisted.
  const [rawKey, setRawKey] = useState<string | null>(null);
  const [issuedPrefix, setIssuedPrefix] = useState<string | null>(null);

  async function refresh() {
    setLoadError(null);
    try {
      const list = await apiAdmin.internalKeys.list(productId);
      setRows(list);
      setForbidden(false);
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setRows([]);
        return;
      }
      if (err instanceof ApiError) setLoadError(err);
      setRows([]);
    }
  }

  useEffect(() => {
    // `refresh()` only setStates after an `await`; wrap it in an inline async
    // IIFE so the effect body holds no synchronous setState (behavior identical).
    void (async () => {
      await refresh();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  async function handleRevoke() {
    if (!confirmRevoke) return;
    setBusy(true);
    try {
      // Revoke is idempotent — revoking an already-REVOKED key resolves
      // successfully and simply leaves the row REVOKED.
      await apiAdmin.internalKeys.revoke(productId, confirmRevoke.id);
      snack.success(t('internalKeys.revoked', 'Key turned off.'));
      setConfirmRevoke(null);
      await refresh();
    } catch (err) {
      if (isForbidden(err)) {
        setForbidden(true);
        setConfirmRevoke(null);
        return;
      }
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
      setConfirmRevoke(null);
    } finally {
      setBusy(false);
    }
  }

  // Drop the raw key from state on dismiss — never persisted past this point.
  function dismissRawKey() {
    setRawKey(null);
    setIssuedPrefix(null);
    void refresh();
  }

  if (forbidden) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h3" gutterBottom>
          {t('internalKeys.title', 'Internal API keys')}
        </Typography>
        <Alert severity="warning">
          {t(
            'internalKeys.superAdminRequired',
            'Super admin required to manage internal API keys.',
          )}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="h3">{t('internalKeys.title', 'Internal API keys')}</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setIssueOpen(true)}>
          {t('internalKeys.issue', 'Issue key')}
        </Button>
      </Stack>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 2,
        }}
      >
        {t(
          'internalKeys.explanation',
          'Each key is the X-Internal-Api-Key a product backend presents to saas-auth. A product can have several active keys at once for zero-downtime rotation. The raw key is shown only once at creation — saas-auth stores only a hash.',
        )}
      </Typography>
      <ErrorBanner error={loadError} />
      {rows === null ? (
        <Typography
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('common.loading', 'Loading')}
        </Typography>
      ) : rows.length === 0 ? (
        <Typography
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('internalKeys.empty', 'No internal keys issued for this product yet.')}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('internalKeys.columns.keyPrefix', 'Key prefix')}</TableCell>
                <TableCell>{t('internalKeys.columns.label', 'Label')}</TableCell>
                <TableCell>{t('internalKeys.columns.status', 'Status')}</TableCell>
                <TableCell>{t('internalKeys.columns.created', 'Created')}</TableCell>
                <TableCell>{t('internalKeys.columns.revoked', 'Revoked')}</TableCell>
                <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((key) => {
                const revoked = key.status === 'REVOKED';
                return (
                  <TableRow key={key.id}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                        }}
                      >
                        {key.keyPrefix}
                        {'…'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {key.label || (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {t('internalKeys.noLabel', '—')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(`internalKeys.statuses.${key.status}`, key.status)}
                        color={revoked ? 'default' : 'success'}
                        size="small"
                        variant={revoked ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatTimestamp(key.createdAt)}</Typography>
                      {key.createdBy ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {key.createdBy}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {key.revokedAt ? formatTimestamp(key.revokedAt) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label={t('internalKeys.revoke', 'Turn off')}
                        title={t('internalKeys.revoke', 'Turn off')}
                        disabled={revoked}
                        onClick={() => setConfirmRevoke(key)}
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <IssueKeyDialog
        open={issueOpen}
        productId={productId}
        onClose={() => setIssueOpen(false)}
        onForbidden={() => {
          setIssueOpen(false);
          setForbidden(true);
        }}
        onIssued={(res) => {
          setIssueOpen(false);
          // Hold the raw key transiently for the once-only display. The issue
          // response nests the metadata under `key` (InternalKeyCreatedDto).
          setRawKey(res.rawKey);
          setIssuedPrefix(res.key.keyPrefix);
        }}
      />
      {/* Once-only raw-key reveal. Reuses the shared secret-once dialog (cannot
          be dismissed by Escape/backdrop; acknowledge is hold-gated). On
          acknowledge the raw key is dropped from state. */}
      <SecretOnceDialog
        open={rawKey !== null}
        secret={rawKey ?? ''}
        label={t('internalKeys.reveal.title', 'Copy this internal key now')}
        extras={
          <Stack spacing={1}>
            {issuedPrefix ? (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {t('internalKeys.reveal.prefix', 'Prefix: {{prefix}}', { prefix: issuedPrefix })}
              </Typography>
            ) : null}
            <Alert severity="info">
              {t(
                'internalKeys.reveal.secureTransmit',
                "Deliver this key to the product backend owner over a secure channel (e.g. a password manager share) — it becomes that backend's X-Internal-Api-Key. saas-auth keeps only a hash; this value cannot be retrieved again.",
              )}
            </Alert>
          </Stack>
        }
        onAcknowledge={dismissRawKey}
      />
      <ConfirmDialog
        open={Boolean(confirmRevoke)}
        title={t('internalKeys.revokeTitle', 'Turn off this key?')}
        body={t(
          'internalKeys.revokeBody',
          'Turn off the key {{prefix}}…? A backend still using it will be rejected immediately. This cannot be undone — issue a new key to rotate.',
          { prefix: confirmRevoke?.keyPrefix ?? '' },
        )}
        severity="error"
        confirmLabel={t('internalKeys.revoke', 'Turn off')}
        busy={busy}
        onConfirm={() => void handleRevoke()}
        onCancel={() => setConfirmRevoke(null)}
      />
    </Paper>
  );
}

interface IssueKeyDialogProps {
  open: boolean;
  productId: number;
  onClose: () => void;
  onForbidden: () => void;
  onIssued: (res: IssueInternalKeyResponse) => void;
}

function IssueKeyDialog({ open, productId, onClose, onForbidden, onIssued }: IssueKeyDialogProps) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [label, setLabel] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [prevOpen, setPrevOpen] = useState(open);
  const [pending, setPending] = useState(false);

  // Clear the form on the open transition via a render-phase state adjustment
  // (React's "adjust state while rendering" pattern) instead of a synchronous
  // setState in an effect — same result, no inter-render flash.
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setLabel('');
      setError(null);
    }
  }

  async function onSubmit() {
    setError(null);
    setPending(true);
    try {
      const trimmed = label.trim();
      const res = await apiAdmin.internalKeys.issue(productId, trimmed ? { label: trimmed } : {});
      snack.success(t('internalKeys.issued', 'Internal key issued.'));
      onIssued(res);
    } catch (err) {
      if (isForbidden(err)) {
        onForbidden();
        return;
      }
      if (err instanceof ApiError) setError(err);
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('internalKeys.issueTitle', 'Issue internal API key')}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={error} />
        <Box
          component="form"
          id="internal-key-form"
          onSubmit={(e) => {
            e.preventDefault();
            void onSubmit();
          }}
          noValidate
        >
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t(
                'internalKeys.issueBody',
                'A new high-entropy key is generated by saas-auth and shown once. Give it an optional label to identify which backend it belongs to.',
              )}
            </Typography>
            <TextField
              fullWidth
              label={t('internalKeys.fields.label', 'Label (optional)')}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t('internalKeys.fields.labelPlaceholder', 'e.g. dink-api prod')}
              helperText={t(
                'internalKeys.fields.labelHelper',
                'A human note shown in the list. Optional.',
              )}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button form="internal-key-form" type="submit" variant="contained" disabled={pending}>
          {t('internalKeys.issue', 'Issue key')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Render a key timestamp. The shipped SASATH-0052 emits Java `LocalDateTime`
 * ISO-8601 local strings (e.g. `2026-06-19T18:30:00`), which `new Date(...)`
 * parses as local time — so the value is rendered directly. The empty guard
 * covers a null `revokedAt` (an ACTIVE key has none).
 */
function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}
